'use client';

// Contact Info — phone, email, address, hours.

import { Phone, Envelope, MapPin, Clock, WhatsappLogo } from '@phosphor-icons/react';
import type { ContactInfoConfig } from '@/lib/structured-builder/widget-types/contact-info';

type Props = { config: ContactInfoConfig; locale: string };

export function ContactInfoWidget({ config, locale }: Props) {
  const isAr = locale === 'ar';
  const heading = isAr ? config.heading_ar : config.heading_en;
  const address = isAr ? config.address_ar : config.address_en;
  const hours = isAr ? config.hours_ar : config.hours_en;
  const alignCls = config.align === 'center' ? 'text-center items-center mx-auto' : '';

  const items: { icon: React.ReactNode; text: string; href?: string }[] = [];
  if (config.phone) items.push({ icon: <Phone className="h-5 w-5 text-primary" />, text: config.phone, href: `tel:${config.phone}` });
  if (config.whatsapp) items.push({ icon: <WhatsappLogo className="h-5 w-5 text-[#25D366]" />, text: config.whatsapp, href: `https://wa.me/${config.whatsapp.replace(/\D/g, '')}` });
  if (config.email) items.push({ icon: <Envelope className="h-5 w-5 text-primary" />, text: config.email, href: `mailto:${config.email}` });
  if (address) items.push({ icon: <MapPin className="h-5 w-5 text-primary" />, text: address });
  if (hours) items.push({ icon: <Clock className="h-5 w-5 text-primary" />, text: hours });

  if (config.style === 'grid') {
    return (
      <div className="w-full">
        {heading && <h2 className="text-2xl font-bold text-foreground text-center mb-6">{heading}</h2>}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <div key={i} className="flex flex-col items-center gap-2 p-5 rounded-xl bg-card border border-border">
              {item.icon}
              {item.href ? <a href={item.href} className="text-sm text-foreground hover:text-primary transition-colors" dir="ltr">{item.text}</a> : <span className="text-sm text-foreground">{item.text}</span>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (config.style === 'inline') {
    return (
      <div className={`flex flex-wrap items-center gap-6 ${alignCls ? 'justify-center' : 'justify-start'}`}>
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            {item.icon}
            {item.href ? <a href={item.href} className="text-sm text-foreground hover:text-primary transition-colors" dir="ltr">{item.text}</a> : <span className="text-sm text-foreground">{item.text}</span>}
          </div>
        ))}
      </div>
    );
  }

  // card (default)
  return (
    <div className={`flex flex-col ${alignCls} max-w-md`}>
      {heading && <h2 className="text-xl font-bold text-foreground mb-4">{heading}</h2>}
      <div className="flex flex-col gap-4 p-6 rounded-2xl bg-card border border-border">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-3">
            {item.icon}
            {item.href ? <a href={item.href} className="text-sm text-foreground hover:text-primary transition-colors" dir="ltr">{item.text}</a> : <span className="text-sm text-foreground">{item.text}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
